import { CLAIM_KIND_LABELS, plural } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Check, FileSearch, Lock, X } from 'lucide-react';
import { DataNumber, DataText } from '../components/data/data-text';
import { StatusBadge } from '../components/entities/entity-link';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { ClaimLine, dateOf } from '../components/review/inbox';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { ApiError } from '../lib/api';
import { useInboxWrite, useUnreviewedFacts } from '../lib/review-inbox';
import { useSchema } from '../lib/schema';

// An upload added to the graph without review (task 21): the unreviewed facts that rest on it, each verified or
// rejected here, or all verified at once. Everything is already visible in the graph, marked unreviewed.

export const Route = createFileRoute('/review/unreviewed/$id')({ component: UnreviewedFactsPage });

function UnreviewedFactsPage() {
  const { id } = Route.useParams();
  const facts = useUnreviewedFacts(id);
  const { data: snap } = useSchema();
  const write = useInboxWrite();
  const confirm = useConfirm();
  const back = (
    <Link to="/review" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" aria-hidden="true" /> Review Inbox
    </Link>
  );
  if (facts.isError)
    return (
      <div className="space-y-4">
        {back}
        <EmptyState icon={FileSearch} title="Nothing to check here" learnMore="review-inbox">
          {facts.error instanceof ApiError ? facts.error.message : 'Try again in a moment.'}
        </EmptyState>
      </div>
    );
  if (!facts.data || !snap) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { document: doc, claims, entities } = facts.data;
  const verifyAll = async () => {
    const ok = await confirm({
      title: 'Verify every fact?',
      body: <>All {plural(claims.length, 'unreviewed fact')} from this file become verified, recorded as checked by you.</>,
      confirmLabel: 'Verify all',
      cancelLabel: 'Not yet',
    });
    if (ok) await write.mutateAsync({ kind: 'verify-all', documentId: doc.id }).catch(() => {});
  };
  return (
    <div className="space-y-4">
      {back}
      <PageHeader
        title={doc.filename}
        description={[
          'Added to the graph without review',
          doc.uploader ? `uploaded by ${doc.uploader}` : null,
          dateOf(doc.createdAt),
          doc.sensitivity === 'confidential' ? 'Confidential' : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      <Tip id="review-unreviewed" learnMore="review-inbox">
        These facts are already in the graph, shown with a dashed border as unreviewed. Verify what the source supports and reject what it
        doesn’t; rejected facts leave profiles but stay in the history.
      </Tip>
      <section className="flex flex-wrap items-center gap-3 rounded-lg border bg-surface px-3 py-2" aria-label="Unreviewed facts">
        <span className="text-sm" data-testid="unreviewed-count">
          <DataNumber>{plural(claims.length, 'unreviewed fact')}</DataNumber>
        </span>
        {doc.sensitivity === 'confidential' && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3" aria-hidden="true" /> Confidential
          </span>
        )}
        <Link to="/library/$id" params={{ id: doc.id }} className="text-sm underline underline-offset-2">
          Open in the Library
        </Link>
        {claims.length > 0 && (
          <Button size="sm" className="ms-auto" disabled={write.isPending} onClick={verifyAll}>
            <Check className="size-4" aria-hidden="true" /> Verify all
          </Button>
        )}
      </section>
      {write.error && (
        <p role="alert" className="text-sm text-danger-foreground">
          {write.error}
        </p>
      )}
      {claims.length === 0 ? (
        <EmptyState icon={FileSearch} title="Everything from this file has been checked" learnMore="review-inbox">
          Auto-matches from this file, if any, are listed in the Review Inbox.
        </EmptyState>
      ) : (
        <ul className="space-y-2">
          {claims.map(c => (
            <li key={c.id} className="space-y-1 rounded-lg border border-dashed bg-surface p-3" data-claim={c.id}>
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1 space-y-1 text-sm">
                  <ClaimLine claim={c} entities={entities} snap={snap} />
                  <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                    <StatusBadge status={c.status} />
                    {c.claimKind !== 'fact' && <span>{CLAIM_KIND_LABELS[c.claimKind]}</span>}
                    {c.autoMatched && <span className="rounded bg-muted px-1.5 py-0.5">Auto-matched</span>}
                    {c.periodText && <DataText>{c.periodText}</DataText>}
                  </p>
                  {(c.evidence ?? []).map(e => (
                    <p key={`${e.documentId}-${e.quote}`} className="text-xs text-muted-foreground">
                      “<DataText>{e.quote}</DataText>”
                    </p>
                  ))}
                </div>
                <span className="flex gap-1">
                  <Button
                    size="sm"
                    disabled={write.isPending}
                    onClick={() =>
                      write.mutateAsync({ kind: 'claim', documentId: doc.id, claimId: c.id, status: 'verified' }).catch(() => {})
                    }
                  >
                    <Check className="size-4" aria-hidden="true" /> Verify
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={write.isPending}
                    onClick={() =>
                      write.mutateAsync({ kind: 'claim', documentId: doc.id, claimId: c.id, status: 'rejected' }).catch(() => {})
                    }
                  >
                    <X className="size-4" aria-hidden="true" /> Reject
                  </Button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
